import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Modal, Loader, Segment } from 'semantic-ui-react';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PrintIcon from '@material-ui/icons/Print';

import { AppState } from 'services/types';
import {
    ADMIN,
    MANAGER,
    ASSISTANT_MANAGER
} from 'components/RoleGate/constants';
// @ts-ignore
import ErrorBoundaryAlert from 'components/ErrorBoundaryAlert';
import RoleGate from 'components/RoleGate';
import ButtonLink from 'components/ButtonLink';
// @ts-ignore
import { ErrorBoundary } from 'workspaces/redux-error-boundary';
import { WorksheetPageIndex, WorksheetType } from 'services/worksheet/types';
import {
    makeSelectQueryStringBooleanValue,
    makeSelectQueryStringValue
    // @ts-ignore
} from 'workspaces/react-router-selectors';
import { updateQueryString } from 'services/router/actions';
import LoadingGate from 'components/LoadingGate';
// @ts-ignore
import ErrorGate from 'components/ErrorGate';
import {
    WORKSHEET_LOAD,
    WORKSHEET_TYPE_FERTILIZER
} from 'services/worksheet/constants';

import PrintSprayWorksheetPage from 'scenes/Spray/scenes/Diary/components/PrintSprayWorksheetModal/components/PrintSprayWorksheetPage';
import FertilizerPrintWorksheetPage from 'scenes/Fertilizer/scenes/Diary/components/FertilizerPrintWorksheetModal/components/FertilizerPrintWorksheetPage';
import WorksheetRequest from 'components/WorksheetRequest';
import { makeSelectWorksheetPages } from './selectors';

import { PRINT_WORKSHEET_MODAL } from './constants';
import './styles.scss';

interface ConnectedProps {
    isOpen: boolean;
    pages?: any;
    worksheetOperatorPersonId: ReturnType<
        ReturnType<typeof makeSelectQueryStringValue>
    >;
    worksheetDate: ReturnType<ReturnType<typeof makeSelectQueryStringValue>>;
}

interface DispatchProps {
    updateQueryString: typeof updateQueryString;
}

interface OwnProps {
    worksheetType: WorksheetType;
}

type Props = ConnectedProps & DispatchProps & OwnProps;

export class PrintWorksheetModalComponent extends React.PureComponent<Props> {
    componentDidMount(): void {
        this.updatePrintStyleSettings();
    }

    componentDidUpdate(): void {
        this.updatePrintStyleSettings();
    }

    /*
        Checkes whether modal is open and then applies
        specific style into modals div wrapper and root element.
        These are necessary to make sure that print styles work.
     */
    updatePrintStyleSettings = (): void => {
        const { isOpen } = this.props;
        const root = document.getElementById('root');

        if (isOpen) {
            // Gets the modals wrapper element (semantic modal)
            const modals = document.getElementsByClassName('modals')[0];

            if (root) {
                // Add no-print styles into root, as root styles do affect printing modal styles, so we hide root while in print mode.
                root.classList.add('no-print');
            }

            if (modals) {
                // Remove modals wrapper class, as these affect modal printing styles
                modals.removeAttribute('class');
                // Add our custom printing wrapper style
                modals.classList.add('print-wrapper');
            }
        } else if (root) {
            // Removes the printing mode class, so root can go back to normal
            root.classList.remove('no-print');
        }
    };

    handleClose = (): void => {
        this.props.updateQueryString({
            [PRINT_WORKSHEET_MODAL]: undefined,
            worksheetOperatorPersonId: undefined,
            worksheetDate: undefined
        });
    };

    handlePrint = (): void => {
        window.print();
    };

    renderWorksheetTypePage = (
        pageIndex: WorksheetPageIndex
    ): React.ReactElement => {
        const { worksheetType } = this.props;

        switch (worksheetType) {
            case WORKSHEET_TYPE_FERTILIZER:
                return <FertilizerPrintWorksheetPage pageIndex={pageIndex} />;
            default:
                return <PrintSprayWorksheetPage pageIndex={pageIndex} />;
        }
    };

    render(): React.ReactElement {
        const {
            pages,
            worksheetOperatorPersonId,
            worksheetDate,
            worksheetType,
            isOpen
        } = this.props;

        return (
            <RoleGate requiredRoles={[ADMIN, MANAGER, ASSISTANT_MANAGER]}>
                <Modal
                    className="print-modal"
                    open={isOpen}
                    onClose={this.handleClose}
                    size="large"
                    centered={false}
                >
                    <ErrorBoundary
                        namespace={WORKSHEET_LOAD}
                        FallbackComponent={<ErrorBoundaryAlert />}
                    >
                        <Modal.Header className="print-modal-header">
                            <ButtonLink
                                onClick={this.handleClose}
                                classNames="print-modal-header-button"
                                buttonProps={{ primary: true }}
                            >
                                <React.Fragment>
                                    <ArrowBackIcon className="print-modal-header-icon" />
                                    Back
                                </React.Fragment>
                            </ButtonLink>
                            <ErrorGate
                                namespace={WORKSHEET_LOAD}
                                hideFallbackComponent
                            >
                                <LoadingGate namespace={WORKSHEET_LOAD}>
                                    <ButtonLink
                                        onClick={this.handlePrint}
                                        classNames="print-modal-header-button"
                                        buttonProps={{ primary: true }}
                                    >
                                        <React.Fragment>
                                            <PrintIcon className="print-modal-header-icon" />
                                            Print
                                        </React.Fragment>
                                    </ButtonLink>
                                </LoadingGate>
                            </ErrorGate>
                        </Modal.Header>
                        <Modal.Content className="print-modal-content">
                            <WorksheetRequest
                                worksheetOperatorPersonId={
                                    worksheetOperatorPersonId
                                }
                                worksheetDate={worksheetDate}
                                worksheetType={worksheetType}
                            />
                            <ErrorGate namespace={WORKSHEET_LOAD}>
                                <LoadingGate
                                    namespace={WORKSHEET_LOAD}
                                    LoadingComponent={
                                        <Segment>
                                            <Loader active inline="centered" />
                                        </Segment>
                                    }
                                >
                                    <div className="print-worksheet">
                                        {pages &&
                                            pages.map(
                                                (_page: any, index: number) => (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <React.Fragment key={index}>
                                                        {this.renderWorksheetTypePage(
                                                            index
                                                        )}
                                                        <div className="print-worksheet-page-break">
                                                            &nbsp;
                                                        </div>
                                                    </React.Fragment>
                                                )
                                            )}
                                    </div>
                                </LoadingGate>
                            </ErrorGate>
                        </Modal.Content>
                    </ErrorBoundary>
                </Modal>
            </RoleGate>
        );
    }
}

const mapStateToProps = createStructuredSelector<AppState, {}, ConnectedProps>({
    isOpen: makeSelectQueryStringBooleanValue(PRINT_WORKSHEET_MODAL),
    worksheetOperatorPersonId: makeSelectQueryStringValue(
        'worksheetOperatorPersonId'
    ),
    worksheetDate: makeSelectQueryStringValue('worksheetDate'),
    pages: makeSelectWorksheetPages()
});

export default connect(mapStateToProps, { updateQueryString })(
    PrintWorksheetModalComponent
);
